/**
 * ContactProvenanceBadges — the contact header "draws from" badges (§3.5): a
 * small provider icon per account this contact composes from, shown at-a-glance
 * regardless of view/edit mode. Derives the account set client-side from the
 * union of the contact's element sources + primary link (contactSourceIds), then
 * reuses ProvenanceIcons to render the logos + per-account tooltips. Renders
 * nothing for native/legacy contacts with no provider provenance.
 */
import type { ContactElement, ElementSource } from '../contactProvenance';
import { contactSourceIds } from '../contactProvenance';
import { ProvenanceIcons } from './ProvenanceIcons';

interface ProvenancedContact {
  emailEntries?: ContactElement[];
  phoneEntries?: ContactElement[];
  attributes?: Record<string, unknown> | null;
}

export function ContactProvenanceBadges({ contact, size = 14 }: { contact: ProvenancedContact | null | undefined; size?: number }) {
  const ids = contactSourceIds(contact);
  if (ids.length === 0) return null;
  // Account-level badges: one pseudo-source per account (no signal type → the
  // tooltip shows just "Provider (account email)"), reusing ProvenanceIcons.
  const sources: ElementSource[] = ids.map((providerIntegrationId) => ({ providerIntegrationId, sourceType: '' }));
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      title="Accounts this contact draws from"
    >
      <span style={{ fontSize: 11, color: 'var(--color-text-faint)' }}>from</span>
      <ProvenanceIcons sources={sources} size={size} />
    </span>
  );
}
